import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Mail, User, Calendar, MessageSquare, ArrowLeft } from 'lucide-react';
import { Separator } from './ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ token, onBack, onAuthError }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/contact-messages`, { headers: authHeaders });
      if (response.status === 401) { onAuthError(); return; }
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      const res = await fetch(`${API}/admin/contact-messages/${messageId}/read`, {
        method: 'PUT',
        headers: authHeaders
      });
      if (res.status === 401) { onAuthError(); return; }
      setMessages(messages.map(msg => 
        msg.id === messageId ? { ...msg, isRead: true } : msg
      ));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Button 
              onClick={onBack}
              variant="outline" 
              className="mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Website
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Doodle Bug Admin Panel</h1>
          </div>
          <p className="text-gray-600">Manage your contact messages and customer inquiries</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <MessageSquare className="w-8 h-8 text-blue-500 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
                  <p className="text-gray-600">Total Messages</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Mail className="w-8 h-8 text-green-500 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {messages.filter(msg => !msg.isRead).length}
                  </p>
                  <p className="text-gray-600">Unread Messages</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <User className="w-8 h-8 text-purple-500 mr-3" />
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {messages.filter(msg => msg.isRead).length}
                  </p>
                  <p className="text-gray-600">Read Messages</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Messages */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Messages</CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No messages yet</p>
                <p className="text-gray-400">Messages from your contact form will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div key={message.id || index}>
                    <div className={`p-4 rounded-lg border-l-4 ${
                      message.isRead 
                        ? 'bg-gray-50 border-l-gray-300' 
                        : 'bg-blue-50 border-l-blue-500'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <h3 className="font-semibold text-gray-900 mr-3">
                              {message.name}
                            </h3>
                            {!message.isRead && (
                              <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                                New
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center text-sm text-gray-600 mb-2">
                            <Mail className="w-4 h-4 mr-1" />
                            <span className="mr-4">{message.email}</span>
                            <Calendar className="w-4 h-4 mr-1" />
                            <span>{formatDate(message.createdAt)}</span>
                          </div>
                          
                          <p className="text-gray-800 mb-3 leading-relaxed">
                            {message.message}
                          </p>
                          
                          <div className="flex items-center">
                            <span className="text-sm bg-gray-200 text-gray-700 px-2 py-1 rounded mr-3">
                              {message.type || 'general'}
                            </span>
                            {!message.isRead && (
                              <Button
                                onClick={() => markAsRead(message.id)}
                                size="sm"
                                variant="outline"
                              >
                                Mark as Read
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < messages.length - 1 && <Separator className="my-4" />}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;